<div class="results-container">
  
  <!-- Loading State -->
  <div *ngIf="loading" class="loading-container">
    <mat-spinner diameter="60"></mat-spinner>
    <p class="loading-text">Loading results...</p>
  </div>

  <!-- Results Content -->
  <ng-container *ngIf="!loading && results">
    <!-- Compact Header Cards -->
    <div class="compact-header">
      <div class="compact-card">
        <div class="compact-label">Total Records</div>
        <div class="compact-value">{{ results?.report?.summary?.rows_input || 'N/A' }}</div>
      </div>
      <div class="compact-card" [ngClass]="getTotalAnomalyCount() > 0 ? 'has-anomalies' : 'no-anomalies'">
        <div class="compact-label">Anomalies Found</div>
        <div class="compact-value">{{ getTotalAnomalyCount() }}</div>
      </div>
      <div class="compact-card">
        <div class="compact-label">Execution Time</div>
        <div class="compact-value">{{ getExecutionTimeInSeconds() }}s</div>
      </div>
    </div>

    <!-- Data Summary Section -->
    <div class="summary-section" *ngIf="dataSummary">
      <app-data-summary [summary]="dataSummary" [title]="'Detection Summary'"></app-data-summary>
    </div>

    <div class="results-content">
      <!-- Table-Level Results -->
      <div class="table-level-results" *ngIf="hasTableLevelResults()">
        <mat-expansion-panel class="help-expansion-panel"
                             [expanded]="isTableLevelExpanded()"
                             (opened)="setTableLevelExpanded(true)"
                             (closed)="setTableLevelExpanded(false)">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <mat-icon class="panel-icon">analytics</mat-icon>
              Table-Level Analysis
              <span class="anomaly-badge" 
                    [class.has-anomalies]="getTableLevelAnomalyCount() > 0">
                {{ getTableLevelAnomalyCount() }} anomalies
              </span>
            </mat-panel-title>
            <mat-panel-description>
              <span class="stat-item">
                Rate: {{ (getTableLevelAnomalyRatio() * 100).toFixed(2) }}%
              </span>
            </mat-panel-description>
          </mat-expansion-panel-header>

          <div class="column-details">
            <!-- Detection Method and Score Distribution Row -->
            <div class="table-level-stats-row">
              <!-- Detection Method -->
              <div class="detection-method-col">
                <h5>Detection Method</h5>
                <div class="detection-info">
                  <div class="info-row">
                    <span class="label">Method Family:</span>
                    <span class="value">{{ getMethodFamily() }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">Algorithm{{ getMethodsUsed().length > 1 ? 's' : '' }}:</span>
                    <span class="value">{{ getMethodsUsed().join(', ') }}</span>
                  </div>
                  <div class="info-row" *ngIf="hasTableLevelAnomalies()">
                    <button mat-raised-button color="primary" 
                            (click)="openAnomalyListDialog()"
                            class="download-anomalies-btn">
                      View Anomalies
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- Score Distribution -->
              <div class="viz-col" *ngIf="getTableLevelArtifacts()?.score_histogram">
                <h5>Score Distribution</h5>
                <img [src]="'data:image/png;base64,' + getTableLevelArtifacts()?.score_histogram" 
                     alt="Score Histogram" class="viz-image">
              </div>
            </div>

            <!-- Visualizations -->
            <div class="anomaly-viz-container" *ngIf="getTableLevelArtifacts()">
              <!-- Heatmap -->
              <div class="viz-col" *ngIf="getTableLevelArtifacts()?.heatmap_outlier_positions">
                <h5>Outlier Positions Heatmap</h5>
                <img [src]="'data:image/png;base64,' + getTableLevelArtifacts()?.heatmap_outlier_positions" 
                     alt="Heatmap" class="viz-image">
              </div>
              
              <!-- PCA Scatter -->
              <div class="viz-col" *ngIf="getTableLevelArtifacts()?.pca_scatter">
                <h5>PCA Scatter Plot</h5>
                <img [src]="'data:image/png;base64,' + getTableLevelArtifacts()?.pca_scatter" 
                     alt="PCA Scatter" class="viz-image">
              </div>
              
              <!-- Position Map (full width, displayed last) -->
              <div class="viz-col viz-full-width" *ngIf="getTableLevelArtifacts()?.position_map">
                <h5>Anomaly Position</h5>
                <img [src]="'data:image/png;base64,' + getTableLevelArtifacts()?.position_map" 
                     alt="Position Map" class="viz-image">
              </div>
            </div>
          </div>
        </mat-expansion-panel>
      </div>

      <!-- Per-column results -->
      <div class="per-column-results" *ngIf="hasColumnLevelResults()">
        <h3 class="section-title">Column Analysis ({{ getColumnNames().length }} columns)</h3>
        
        <mat-expansion-panel *ngFor="let columnName of getColumnNames()" 
                             class="help-expansion-panel"
                             [expanded]="isColumnExpanded(columnName)"
                             (opened)="setColumnExpanded(columnName, true)"
                             (closed)="setColumnExpanded(columnName, false)">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <mat-icon class="panel-icon">view_column</mat-icon>
              {{ columnName }}
              <span class="anomaly-badge" 
                    [class.has-anomalies]="(getColumnResult(columnName)?.anomaly_count || getColumnAnomalySummary(columnName)?.anomaly_count || 0) > 0">
                {{ getColumnResult(columnName)?.anomaly_count || getColumnAnomalySummary(columnName)?.anomaly_count || 0 }} anomalies
              </span>
            </mat-panel-title>
            <mat-panel-description>
              <span class="stat-item">
                Rate: {{ ((getColumnResult(columnName)?.anomaly_rate || getColumnAnomalySummary(columnName)?.anomaly_rate || 0) * 100).toFixed(2) }}%
              </span>
            </mat-panel-description>
          </mat-expansion-panel-header>

          <div class="column-details">
            <!-- Basic Statistics -->
            <div class="stats-section" *ngIf="isAdvancedMode && getColumnResult(columnName)?.metadata?.basic_stats">
              <h5>Basic Statistics</h5>
              <div class="stats-content">
                <div class="stats-grid">
                  <!-- Row 1 -->
                  <div class="stat-card">
                    <span class="stat-label">Count:</span>
                    <span class="stat-value">{{ formatCount(getColumnResult(columnName)?.metadata?.basic_stats?.count) }}</span>
                  </div>
                  <div class="stat-card">
                    <span class="stat-label">Missing:</span>
                    <span class="stat-value">{{ formatCount(getColumnResult(columnName)?.metadata?.basic_stats?.missing_count) }}</span>
                  </div>
                  <div class="stat-card">
                    <span class="stat-label">Mean:</span>
                    <span class="stat-value">{{ formatNumber(getColumnResult(columnName)?.metadata?.basic_stats?.mean) }}</span>
                  </div>
                  
                  <!-- Row 2 -->
                  <div class="stat-card">
                    <span class="stat-label">Std Dev:</span>
                    <span class="stat-value">{{ formatNumber(getColumnResult(columnName)?.metadata?.basic_stats?.std) }}</span>
                  </div>
                  <div class="stat-card">
                    <span class="stat-label">Min:</span>
                    <span class="stat-value">{{ formatNumber(getColumnResult(columnName)?.metadata?.basic_stats?.min) }}</span>
                  </div>
                  <div class="stat-card">
                    <span class="stat-label">Max:</span>
                    <span class="stat-value">{{ formatNumber(getColumnResult(columnName)?.metadata?.basic_stats?.max) }}</span>
                  </div>
                  
                  <!-- Row 3 -->
                  <div class="stat-card">
                    <span class="stat-label">Q1:</span>
                    <span class="stat-value">{{ formatNumber(getColumnResult(columnName)?.metadata?.basic_stats?.q1) }}</span>
                  </div>
                  <div class="stat-card">
                    <span class="stat-label">Median:</span>
                    <span class="stat-value">{{ formatNumber(getColumnResult(columnName)?.metadata?.basic_stats?.median) }}</span>
                  </div>
                  <div class="stat-card">
                    <span class="stat-label">Q3:</span>
                    <span class="stat-value">{{ formatNumber(getColumnResult(columnName)?.metadata?.basic_stats?.q3) }}</span>
                  </div>
                </div>
                
                <!-- Boxplot -->
                <div class="stats-boxplot" *ngIf="getColumnResult(columnName)?.artifacts?.boxplot">
                  <img [src]="'data:image/png;base64,' + getColumnResult(columnName)?.artifacts?.boxplot" 
                         alt="Boxplot for {{ columnName }}" class="boxplot-image">
                </div>
              </div>
            </div>

            <!-- Time Series Specific: Position Map -->
            <div class="anomaly-viz-container" *ngIf="isAdvancedMode && getColumnResult(columnName)?.artifacts?.position_map && (getColumnResult(columnName)?.anomaly_count > 0 || getColumnAnomalySummary(columnName)?.anomaly_count > 0)">
              <div class="viz-col viz-full-width">
                <h5>Anomaly Position Map</h5>
                <img [src]="'data:image/png;base64,' + getColumnResult(columnName)?.artifacts?.position_map" 
                     alt="Anomaly position map for {{ columnName }}" class="viz-image">
              </div>
            </div>
            
            <!-- Time Series Pattern and Anomaly Details -->
            <div class="anomaly-viz-container" 
                 *ngIf="getColumnResult(columnName)?.artifacts || true">
              
              <!-- Column 1: Anomaly Details -->
              <div class="anomaly-details-col" *ngIf="getColumnResult(columnName)?.anomaly_count > 0 || getColumnAnomalySummary(columnName)?.anomaly_count > 0">
                <h5>Anomaly Details</h5>
                <div class="anomaly-info">
                  <div class="info-row">
                    <span class="label">Anomaly Count:</span>
                    <span class="value">{{ getColumnResult(columnName)?.anomaly_count || getColumnAnomalySummary(columnName)?.anomaly_count }}</span>
                  </div>
                  <div class="info-row">
                    <span class="label">Anomaly Rate:</span>
                    <span class="value">{{ ((getColumnResult(columnName)?.anomaly_rate || getColumnAnomalySummary(columnName)?.anomaly_rate) * 100).toFixed(2) }}%</span>
                  </div>
                  <div class="info-row" *ngIf="getTailsInfo(columnName)?.low">
                    <span class="label">Low Tail Anomalies:</span>
                    <span class="value">{{ getTailsInfo(columnName)?.low }}</span>
                  </div>
                  <div class="info-row" *ngIf="getTailsInfo(columnName)?.high">
                    <span class="label">High Tail Anomalies:</span>
                    <span class="value">{{ getTailsInfo(columnName)?.high }}</span>
                  </div>
                  <!-- Time Series specific: Intensity scores -->
                  <div class="info-row" *ngIf="getIntensityScores(columnName)?.p95_score">
                    <span class="label">95th Percentile Score:</span>
                    <span class="value">{{ formatNumber(getIntensityScores(columnName)?.p95_score, 2) }}</span>
                  </div>
                  <div class="info-row" *ngIf="getIntensityScores(columnName)?.p99_score">
                    <span class="label">99th Percentile Score:</span>
                    <span class="value">{{ formatNumber(getIntensityScores(columnName)?.p99_score, 2) }}</span>
                  </div>
                  <div class="info-row" *ngIf="hasAnomalyExamples(columnName)">
                    <button mat-raised-button color="primary" 
                            (click)="openColumnAnomalyDialog(columnName)"
                            class="download-anomalies-btn">
                      View Anomalies
                    </button>
                  </div>
                </div>
              </div>
              
              <!-- Column 2: Scatter Plot / Frequency Chart / Line Plot -->
              <!-- Simple Mode: Show scatter plot only -->
              <div class="viz-col viz-col-chart" *ngIf="!isAdvancedMode">
                <div class="chart-header-with-hint">
                  <h5>Scatter Plot</h5>
                  <div class="chart-header-actions">
                    <button 
                      mat-icon-button 
                      class="visualise-chart-button" 
                      (click)="openVisualiseDialog(columnName)"
                      matTooltip="Open visualise dialog"
                      [disabled]="!selectedXColumns.get(columnName)">
                      <mat-icon svgIcon="expand"></mat-icon>
                    </button>
                  </div>
                </div>
                <!-- Uninitialized state (no X column selected) -->
                <div *ngIf="!selectedXColumns.get(columnName) && !isColumnChartLoading(columnName)" class="chart-placeholder">
                  <mat-icon>scatter_plot</mat-icon>
                  <p>Select an X-axis column to view scatter plot</p>
                </div>
                <!-- Loading state -->
                <div *ngIf="selectedXColumns.get(columnName) && isColumnChartLoading(columnName)" class="chart-loading">
                  <mat-spinner diameter="40"></mat-spinner>
                  <p>Loading scatter plot...</p>
                </div>
                <!-- Chart display -->
                <app-interactive-chart 
                  *ngIf="selectedXColumns.get(columnName) && !isColumnChartLoading(columnName) && hasColumnChart(columnName)"
                  [chartData]="columnChartData.get(columnName)!"
                  [chartType]="'scatter'"
                  [dataSourceId]="dataSourceId || undefined"
                  [uploadedData]="uploadedFileData"
                  [columnName]="selectedXColumns.get(columnName)!"
                  [secondColumnName]="columnName"
                  [showDataTable]="false"
                  [anomalyIndices]="getColumnAnomalyIndices(columnName)"
                  (selectionChange)="onScatterSelectionChange(columnName, $event)">
                </app-interactive-chart>
                <!-- Error state -->
                <div *ngIf="selectedXColumns.get(columnName) && !isColumnChartLoading(columnName) && !hasColumnChart(columnName) && getColumnChartError(columnName)" 
                     class="chart-error">
                  <mat-icon>error_outline</mat-icon>
                  <p>{{ getColumnChartError(columnName) }}</p>
                </div>
                <!-- X-axis selector (centered below chart) -->
                <div class="x-axis-selector-below" *ngIf="getAvailableXColumns(columnName).length > 0">
                  <mat-form-field class="x-axis-selector">
                    <mat-label>X-Axis Column</mat-label>
                    <mat-select [value]="selectedXColumns.get(columnName) || getDefaultXColumn(columnName)"
                                (selectionChange)="onXColumnChange(columnName, $event.value)">
                      <mat-option *ngFor="let col of getAvailableXColumns(columnName)" [value]="col">
                        {{ col }}
                      </mat-option>
                    </mat-select>
                  </mat-form-field>
                </div>
              </div>
              
              <!-- Advanced Mode: Show lineplot/histogram and scatter -->
              <ng-container *ngIf="isAdvancedMode">
                <!-- Time series lineplot -->
                <div class="viz-col viz-col-chart" *ngIf="getColumnResult(columnName)?.artifacts?.lineplot">
                  <h5>Time Series Pattern</h5>
                  <img [src]="'data:image/png;base64,' + getColumnResult(columnName)?.artifacts?.lineplot" 
                       alt="Time series pattern for {{ columnName }}" class="viz-image">
                </div>
                <!-- Scatter plot in advanced mode (moved above Frequency Chart) -->
                <div class="viz-col viz-col-chart">
                  <div class="chart-header-with-hint">
                    <h5>Scatter Plot</h5>
                    <div class="chart-header-actions">
                      <button 
                        mat-icon-button 
                        class="visualise-chart-button" 
                        (click)="openVisualiseDialog(columnName)"
                        matTooltip="Open visualise dialog"
                        [disabled]="!selectedXColumns.get(columnName)">
                        <mat-icon svgIcon="expand"></mat-icon>
                      </button>
                    </div>
                  </div>
                  <!-- Uninitialized state (no X column selected) -->
                  <div *ngIf="!selectedXColumns.get(columnName) && !isColumnChartLoading(columnName)" class="chart-placeholder">
                    <mat-icon>scatter_plot</mat-icon>
                    <p>Select an X-axis column to view scatter plot</p>
                  </div>
                  <!-- Loading state -->
                  <div *ngIf="selectedXColumns.get(columnName) && isColumnChartLoading(columnName)" class="chart-loading">
                    <mat-spinner diameter="40"></mat-spinner>
                    <p>Loading scatter plot...</p>
                  </div>
                  <!-- Chart display -->
                  <app-interactive-chart 
                    *ngIf="selectedXColumns.get(columnName) && !isColumnChartLoading(columnName) && hasColumnChart(columnName)"
                    [chartData]="columnChartData.get(columnName)!"
                    [chartType]="'scatter'"
                    [dataSourceId]="dataSourceId || undefined"
                    [uploadedData]="uploadedFileData"
                    [columnName]="selectedXColumns.get(columnName)!"
                    [secondColumnName]="columnName"
                    [showDataTable]="false"
                    [anomalyIndices]="getColumnAnomalyIndices(columnName)"
                    (selectionChange)="onScatterSelectionChange(columnName, $event)">
                  </app-interactive-chart>
                  <!-- Error state -->
                  <div *ngIf="selectedXColumns.get(columnName) && !isColumnChartLoading(columnName) && !hasColumnChart(columnName) && getColumnChartError(columnName)" 
                       class="chart-error">
                    <mat-icon>error_outline</mat-icon>
                    <p>{{ getColumnChartError(columnName) }}</p>
                  </div>
                  <!-- X-axis selector (centered below chart) -->
                  <div class="x-axis-selector-below" *ngIf="getAvailableXColumns(columnName).length > 0">
                    <mat-form-field class="x-axis-selector">
                      <mat-label>X-Axis Column</mat-label>
                      <mat-select [value]="selectedXColumns.get(columnName) || getDefaultXColumn(columnName)"
                                  (selectionChange)="onXColumnChange(columnName, $event.value)">
                        <mat-option *ngFor="let col of getAvailableXColumns(columnName)" [value]="col">
                          {{ col }}
                        </mat-option>
                      </mat-select>
                    </mat-form-field>
                  </div>
                </div>
                <!-- Histogram for non-time-series (moved below Scatter Plot) -->
                <div class="viz-col viz-col-chart" *ngIf="!getColumnResult(columnName)?.artifacts?.lineplot && getColumnResult(columnName)?.artifacts?.histogram">
                  <h5>Frequency Chart</h5>
                  <img [src]="'data:image/png;base64,' + getColumnResult(columnName)?.artifacts?.histogram" 
                       alt="Frequency chart for {{ columnName }}" class="viz-image">
                </div>
              </ng-container>
            </div>
          </div>
        </mat-expansion-panel>
      </div>

      <!-- No anomalies message -->
      <div *ngIf="results?.report?.summary && !results.report.summary.any_anomalies" 
           class="no-anomalies">
        <mat-icon svgIcon="check_circle"></mat-icon>
        <p>No anomalies detected in the data</p>
      </div>

      <!-- Warnings -->
      <!-- <div *ngIf="results?.report?.warnings && results.report.warnings.length > 0" 
           class="warnings-section">
        <h3 class="section-title">
          <mat-icon svgIcon="error"></mat-icon>
          Warnings
        </h3>
        <ul class="warnings-list">
          <li *ngFor="let warning of results.report.warnings">{{ warning }}</li>
        </ul>
      </div> -->
    </div>
  </ng-container>

  <!-- No Results State -->
  <div *ngIf="!loading && !results" class="no-results">
    <mat-icon>info</mat-icon>
    <p>No results available</p>
  </div>
</div>
